import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ImagePlus, FolderTree, Package, GripVertical } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const TYPES = [["prodotto", "Prodotto"], ["servizio", "Servizio"]];

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/catalog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Immagine caricata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
        {value ? <img src={imgSrc(value)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImagePlus className="h-5 w-5" /></div>}
      </div>
      <label className="cursor-pointer">
        <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={testid} />
        <span className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1 h-3.5 w-3.5" />} Carica immagine</span>
      </label>
      {value && <Button variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testid}-clear`}>Rimuovi</Button>}
    </div>
  );
}

const EMPTY_CAT = { name: "", description: "", image_url: "", type: "prodotto" };
const EMPTY_PROD = { name: "", description: "", image_url: "", type: "prodotto", category_id: null, costo_punti: 0, specs: [], active: true };

export default function EditCatalogo() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [catDlg, setCatDlg] = useState(null);
  const [prodDlg, setProdDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragCat = useRef(null);
  const dragProd = useRef(null);
  const reorder = (arr, from, to) => { const a = [...arr]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; };
  const onCatDrop = async (to) => { const from = dragCat.current; dragCat.current = null; if (from == null || from === to) return; const a = reorder(cats, from, to); setCats(a); try { await api.post("/catalog/categories/reorder", { ids: a.map((c) => c.id) }); toast.success("Ordine categorie aggiornato"); } catch { toast.error("Riordino non salvato"); load(); } };
  const onProdDrop = async (to) => { const from = dragProd.current; dragProd.current = null; if (from == null || from === to) return; const a = reorder(prods, from, to); setProds(a); try { await api.post("/catalog/products/reorder", { ids: a.map((p) => p.id) }); toast.success("Ordine elementi aggiornato"); } catch { toast.error("Riordino non salvato"); load(); } };

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/catalog/categories"), api.get("/catalog/products")]);
    setCats(c.data || []);
    setProds(p.data || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  const saveCat = async () => {
    if (!catDlg.name.trim()) { toast.error("Nome categoria obbligatorio"); return; }
    setSaving(true);
    try {
      if (catDlg.id) await api.put(`/catalog/categories/${catDlg.id}`, catDlg);
      else await api.post("/catalog/categories", catDlg);
      toast.success("Categoria salvata"); setCatDlg(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setSaving(false); }
  };
  const delCat = async (c) => { if (!window.confirm(`Eliminare la categoria "${c.name}"?`)) return; await api.delete(`/catalog/categories/${c.id}`); await load(); toast.success("Categoria eliminata"); };

  const saveProd = async () => {
    if (!prodDlg.name.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!prodDlg.category_id) { toast.error("Categoria obbligatoria"); return; }
    if (prodDlg.costo_punti === "" || prodDlg.costo_punti == null || Number(prodDlg.costo_punti) < 0) { toast.error("Costo in Punti Vesta obbligatorio"); return; }
    setSaving(true);
    const payload = { ...prodDlg, costo_punti: Number(prodDlg.costo_punti) };
    try {
      if (prodDlg.id) await api.put(`/catalog/products/${prodDlg.id}`, payload);
      else await api.post("/catalog/products", payload);
      toast.success("Elemento salvato"); setProdDlg(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setSaving(false); }
  };
  const delProd = async (p) => { if (!window.confirm(`Eliminare "${p.name}"?`)) return; await api.delete(`/catalog/products/${p.id}`); await load(); toast.success("Eliminato"); };

  const setSpec = (i, k, v) => setProdDlg((d) => { const specs = [...d.specs]; specs[i] = { ...specs[i], [k]: v }; return { ...d, specs }; });
  const addSpec = () => setProdDlg((d) => ({ ...d, specs: [...d.specs, { label: "", value: "" }] }));
  const rmSpec = (i) => setProdDlg((d) => ({ ...d, specs: d.specs.filter((_, j) => j !== i) }));

  return (
    <div className="space-y-8" data-testid="edit-catalogo-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Edit Catalogo</h1>
          <p className="text-sm text-muted-foreground">Gestisci categorie, prodotti e servizi del Catalogo Vesta. Trascina le righe (icona ⣿) per riordinarle: l'ordine si riflette nella vetrina.</p>
        </div>
      </div>

      {/* CATEGORIE */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading text-lg font-bold"><FolderTree className="h-5 w-5 text-primary" /> Categorie</div>
          <Button size="sm" onClick={() => setCatDlg({ ...EMPTY_CAT })} data-testid="add-category-btn"><Plus className="mr-1 h-4 w-4" /> Nuova categoria</Button>
        </div>
        {cats.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna categoria. Creane una per iniziare.</p> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cats.map((c, ci) => (
              <div key={c.id} draggable onDragStart={() => (dragCat.current = ci)} onDragOver={(e) => e.preventDefault()} onDrop={() => onCatDrop(ci)} className="flex gap-2 rounded-lg border border-border p-3 transition-shadow hover:shadow-md" data-testid={`category-row-${c.id}`}>
                <div className="flex cursor-move items-center text-muted-foreground" title="Trascina per riordinare" data-testid={`drag-category-${c.id}`}><GripVertical className="h-4 w-4" /></div>
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-muted">{c.image_url ? <img src={imgSrc(c.image_url)} alt="" className="h-full w-full object-cover" /> : null}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><span className="truncate font-semibold">{c.name}</span><Badge variant="outline" className="text-[10px]">{c.type === "servizio" ? "Servizio" : "Prodotto"}</Badge></div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{c.description}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCatDlg({ ...c })} data-testid={`edit-category-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delCat(c)} data-testid={`delete-category-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* PRODOTTI / SERVIZI */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading text-lg font-bold"><Package className="h-5 w-5 text-primary" /> Prodotti e Servizi</div>
          <Button size="sm" onClick={() => setProdDlg({ ...EMPTY_PROD, category_id: cats[0]?.id || null })} disabled={cats.length === 0} data-testid="add-product-btn"><Plus className="mr-1 h-4 w-4" /> Nuovo elemento</Button>
        </div>
        {cats.length === 0 ? <p className="text-sm text-muted-foreground">Crea prima una categoria.</p> : prods.length === 0 ? <p className="text-sm text-muted-foreground">Nessun prodotto/servizio.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <th className="w-8 p-2"></th><th className="p-2">Immagine</th><th className="p-2">Nome</th><th className="p-2">Tipo</th><th className="p-2">Categoria</th><th className="p-2">Costo (Punti Vesta)</th><th className="p-2">Stato</th><th className="p-2 text-right">Azioni</th>
              </tr></thead>
              <tbody>
                {prods.map((p, pi) => (
                  <tr key={p.id} draggable onDragStart={() => (dragProd.current = pi)} onDragOver={(e) => e.preventDefault()} onDrop={() => onProdDrop(pi)} className="border-b border-border/50 hover:bg-accent/40" data-testid={`product-row-${p.id}`}>
                    <td className="cursor-move p-2 text-muted-foreground" title="Trascina per riordinare" data-testid={`drag-product-${p.id}`}><GripVertical className="h-4 w-4" /></td>
                    <td className="p-2"><div className="h-10 w-10 overflow-hidden rounded bg-muted">{p.image_url ? <img src={imgSrc(p.image_url)} alt="" className="h-full w-full object-cover" /> : null}</div></td>
                    <td className="p-2 font-medium">{p.name}</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">{p.type === "servizio" ? "Servizio" : "Prodotto"}</Badge></td>
                    <td className="p-2 text-muted-foreground">{catName(p.category_id)}</td>
                    <td className="p-2 font-mono font-bold text-primary">{p.costo_punti}</td>
                    <td className="p-2">{p.active ? <Badge className="bg-green-600 text-[10px] text-white">Attivo</Badge> : <Badge variant="secondary" className="text-[10px]">Nascosto</Badge>}</td>
                    <td className="p-2 text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setProdDlg({ ...p, specs: p.specs || [] })} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delProd(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* DIALOG CATEGORIA */}
      <Dialog open={!!catDlg} onOpenChange={(o) => !o && setCatDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="category-dialog">
          <DialogHeader><DialogTitle>{catDlg?.id ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          {catDlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Nome *</Label><Input value={catDlg.name} onChange={(e) => setCatDlg({ ...catDlg, name: e.target.value })} data-testid="category-name-input" /></div>
              <div><Label className="text-xs">Tipo</Label>
                <Select value={catDlg.type} onValueChange={(v) => setCatDlg({ ...catDlg, type: v })}><SelectTrigger data-testid="category-type-select"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Descrizione</Label><Textarea rows={3} value={catDlg.description} onChange={(e) => setCatDlg({ ...catDlg, description: e.target.value })} data-testid="category-desc-input" /></div>
              <div><Label className="text-xs">Immagine</Label><ImageField value={catDlg.image_url} onChange={(u) => setCatDlg({ ...catDlg, image_url: u })} testid="category-image-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDlg(null)}>Annulla</Button>
            <Button onClick={saveCat} disabled={saving} data-testid="category-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG PRODOTTO/SERVIZIO */}
      <Dialog open={!!prodDlg} onOpenChange={(o) => !o && setProdDlg(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{prodDlg?.id ? "Modifica elemento" : "Nuovo prodotto / servizio"}</DialogTitle></DialogHeader>
          {prodDlg && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="text-xs">Nome *</Label><Input value={prodDlg.name} onChange={(e) => setProdDlg({ ...prodDlg, name: e.target.value })} data-testid="product-name-input" /></div>
                <div><Label className="text-xs">Tipo</Label>
                  <Select value={prodDlg.type} onValueChange={(v) => setProdDlg({ ...prodDlg, type: v })}><SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Categoria *</Label>
                  <Select value={prodDlg.category_id || ""} onValueChange={(v) => setProdDlg({ ...prodDlg, category_id: v })}><SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Costo (Punti Vesta) *</Label><Input type="number" min="0" value={prodDlg.costo_punti} onChange={(e) => setProdDlg({ ...prodDlg, costo_punti: e.target.value })} data-testid="product-cost-input" /></div>
              </div>
              <div><Label className="text-xs">Descrizione</Label><Textarea rows={3} value={prodDlg.description} onChange={(e) => setProdDlg({ ...prodDlg, description: e.target.value })} data-testid="product-desc-input" /></div>
              <div><Label className="text-xs">Immagine</Label><ImageField value={prodDlg.image_url} onChange={(u) => setProdDlg({ ...prodDlg, image_url: u })} testid="product-image-input" /></div>
              <div>
                <div className="mb-1 flex items-center justify-between"><Label className="text-xs">Tabella specifiche</Label><Button variant="outline" size="sm" onClick={addSpec} data-testid="add-spec-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Riga</Button></div>
                <div className="space-y-2">
                  {prodDlg.specs.map((s, i) => (
                    <div key={i} className="flex gap-2" data-testid={`spec-row-${i}`}>
                      <Input placeholder="Dato / specifica" value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} data-testid={`spec-label-${i}`} />
                      <Input placeholder="Valore" value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} data-testid={`spec-value-${i}`} />
                      <Button variant="ghost" size="icon" onClick={() => rmSpec(i)} data-testid={`spec-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  ))}
                  {prodDlg.specs.length === 0 && <p className="text-xs text-muted-foreground">Nessuna specifica. Aggiungi righe con dati e valori (es. Potenza · 5 kW).</p>}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={prodDlg.active} onChange={(e) => setProdDlg({ ...prodDlg, active: e.target.checked })} data-testid="product-active-input" /> Visibile nella vetrina</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProdDlg(null)}>Annulla</Button>
            <Button onClick={saveProd} disabled={saving} data-testid="product-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
